/**
 * What a limit against the benchmark was judged on, in words — IMPLEMENTATION.md decision 54.
 *
 * The verdicts list, a verdict's Compliance review and the Excel pane say it this way, and
 * the exported file says the same through `verdictreport.benchmark_says`.
 */

import type { VerdictRow } from './api'
import { amount } from './verdictWords'

/** `EMBIGD as at 2026-09-16: 6.40 years, so between 5.40 years and 7.40 years`. */
export function benchmarkSays(v: Pick<VerdictRow, 'benchmark' | 'threshold' | 'unit' | 'template'>): string {
  const b = v.benchmark
  if (!b) return ''
  const day = b.as_of ? ` as at ${b.as_of}` : ''
  // A share held in countries outside the benchmark (decision 56).
  if (v.template !== 'relative_limit') return `${b.name}${day}: the countries it holds`
  if (b.value === null || b.value === undefined) return `${b.name}${day}, each at its own weight in it`
  const t = (v.threshold ?? {}) as Record<string, number | undefined>
  const value = b.value
  // Per cent of the benchmark's own figure, on a duration (decision 57).
  const proportional = t.benchmark_plus_pct !== undefined || t.benchmark_minus_pct !== undefined
  const below = (n: number) => (proportional ? value * (1 - n / 100) : value - n)
  const above = (n: number) => (proportional ? value * (1 + n / 100) : value + n)
  const minus = proportional ? t.benchmark_minus_pct : t.benchmark_minus
  const plus = proportional ? t.benchmark_plus_pct : t.benchmark_plus
  const low = minus !== undefined ? below(minus) : null
  const high = plus !== undefined ? above(plus) : null
  const said =
    low !== null && high !== null
      ? `between ${amount(low, v.unit)} and ${amount(high, v.unit)}`
      : high !== null
        ? `at most ${amount(high, v.unit)}`
        : `at least ${amount(low ?? 0, v.unit)}`
  return `${b.name}${day}: ${amount(b.value, v.unit)}, so ${said}`
}
